import VideoPlayer from './VideoPlayer.js';

class GoVideoPlayer extends VideoPlayer {
  api_url = '//play.vidyard.com/embed/v4.js';
  script_id = 'vidyard-js-api';
  playOnReady = false;

  _apiLoaded() {
    return !!window.Vidyard;
  }

  _bindApi() {
    // Force Vidyard to scan and render new DOM players
    // Use a small delay to ensure DOM is fully updated
    setTimeout(() => {
      VidyardV4.api.renderDOMPlayers();

      // Get the player by UUID
      const players = VidyardV4.api.getPlayersByUUID(this._videoId());

      if (!players || !players.length) {
        // Retry once after a longer delay
        setTimeout(() => {
          VidyardV4.api.renderDOMPlayers();
          const retryPlayers = VidyardV4.api.getPlayersByUUID(this._videoId());
          if (retryPlayers && retryPlayers.length) {
            this._setupPlayer(retryPlayers[0]);
          }
        }, 500);
        return;
      }

      this._setupPlayer(players[0]);
    }, 100);
  }

  _setupPlayer(player) {
    this.player = player;

    player.on('ready', () => {
      this.trigger(this.ready_event);

      // Desktop autoplay: Unmute if it autoplayed muted
      if (this.opts?.autoplay && !this.isMobile()) {
        this._ensureUnmuted();
      }

      // Mobile: If gesture already happened, play now with sound
      if (this.playOnReady) {
        this.playOnReady = false;

        this._ensureUnmuted();

        if (this.player.play) {
          this.player.play();
        }
      }

      this.setStart();

      // Arm gesture gate on mobile if autoplay requested
      if (this.opts?.autoplay && this.isMobile()) {
        this._armGestureAutoplay();
      }
    });

    player.on('play', () => {});

    player.on('timeupdate', (currentTime) => {
      if (this.opts.preview) return;
      this.setLatestPlayTime(currentTime);
    });

    player.on('playerComplete', () => {
      this.stop();
    });
  }

  // Helper method to ensure player is unmuted with full volume
  _ensureUnmuted() {
    // Try all possible unmute methods
    if (this.player.setMuted) {
      this.player.setMuted(false);
    }
    if (this.player.unmute) {
      this.player.unmute();
    }
    if (this.player.muted !== undefined) {
      this.player.muted = false;
    }

    // Try all possible volume methods
    if (this.player.setVolume) {
      this.player.setVolume(1);
    }
    if (typeof this.player.volume === 'function') {
      this.player.volume(1);
    }
    if (
      this.player.volume !== undefined &&
      typeof this.player.volume !== 'function'
    ) {
      this.player.volume = 1;
    }
  }

  // only 2, 1.5, 1.25, 1 & 0.5 are accepted decimal values
  _changePlaybackRate(speed = 1) {
    this.player.setPlaybackSpeed(speed);
  }

  _duration() {
    return this.player.metadata.length_in_seconds;
  }

  _videoId() {
    let matches = this.constructor.test(this.url);
    return matches[1] || '';
  }

  _play() {
    this.player.play();
  }

  _pause() {
    this.player.pause();
  }

  _stop() {
    this._pause();
  }

  _remove() {
    if (!this.player || !window?.VidyardV4) return;

    try {
      // Remove event listeners first
      this.player.off?.('ready');
      this.player.off?.('play');
      this.player.off?.('timeupdate');
      this.player.off?.('playerComplete');

      // Then destroy the player
      VidyardV4.api.destroyPlayer(this.player);
    } catch (err) {
      console.warn('Error destroying Vidyard player:', err);

      // Manual cleanup fallback
      const id = this.player.uuid || this._videoId();
      delete window.VidyardV4.players?.[id];
    }

    this.player = null;
  }

  _seek(seconds) {
    this.player.seek(seconds);
  }

  buildHtml() {
    // Desktop: allow Vidyard autoplay; Mobile: no autoplay (gesture handles it)
    const autoplayValue = this.opts.autoplay && !this.isMobile() ? '1' : '0';
    return `<img
              class="vidyard-player-embed"
              src="https://play.vidyard.com/${this._videoId()}.jpg"
              data-uuid="${this._videoId()}"
              data-v="4"
              data-type="inline"
              data-autoplay="${autoplayValue}"
            />`;
  }

  wireEvents() {
    if (this.opts?.autoplay && this.isMobile() && !this.gestureArmed) {
      this._armGestureAutoplay(); // will no-op until player exists
    }
  }

  // Override base class method for Vidyard-specific autoplay logic
  async _handleGesturePlay() {
    // If player is already ready, unmute and play
    if (this.ready && this.player) {
      this._ensureUnmuted();

      // Play using Vidyard API
      if (this.player.play) {
        this.player.play();
      }
      return;
    }

    // Player not ready yet - set flag to play when it becomes ready
    this.playOnReady = true;
  }
}

let goVideoPlayerRegex =
  /(?:share|embed)\.vidyard\.com\/(?:watch|share)\/(\w+)/;
// let goVideoPlayerRegex = /share\.vidyard\.com\/watch\/(\w+)/
GoVideoPlayer.test = function (url) {
  return url.match(goVideoPlayerRegex);
};

export default GoVideoPlayer;
